import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertCircle,
  AlertOctagon,
  ArrowDown,
  ArrowUp,
  ChevronLeft,
  ChevronRight,
  Clock,
  Download,
  Filter,
  HardHat,
  Hourglass,
  Loader2,
  PlayCircle,
  Plus,
  RefreshCw,
  Search,
  ShieldCheck,
  SlidersHorizontal,
} from 'lucide-react';
import {
  INTERVENTION_PRIORITES,
  INTERVENTION_TYPES_DEMANDE,
  type InterventionPriorite,
  type InterventionStatut,
  type InterventionTypeDemande,
} from '@mygmao/shared';
import { api, extractErrorMessage } from '../lib/api';
import { usePermissions } from '../hooks/usePermissions';
import { InterventionFormModal } from '../components/interventions/InterventionFormModal';
import { InterventionDetailDrawer } from '../components/interventions/InterventionDetailDrawer';
import { SavedFilterMenu } from '../components/interventions/SavedFilterMenu';
import {
  PrioriteBadge,
  StatutInterventionBadge,
  typeDemandeLabel,
} from '../components/interventions/badges';

/**
 * Page `/interventions` (US-INT-01 / 02 / 09 / 10).
 *
 *  - KPIs en tête + bandeaux d'alerte (urgences / validations).
 *  - Onglets statut (US-INT-09).
 *  - Toolbar filtres rapides + avancés + filtres sauvegardés (US-INT-10).
 *  - Table paginée + tri par colonne.
 *  - Click ligne → drawer 360° (workflow + détails).
 */

interface InterventionRow {
  id: string;
  numero_demande: string;
  titre: string;
  type_demande: InterventionTypeDemande;
  priorite: InterventionPriorite;
  statut: InterventionStatut;
  date_demande: string;
  date_planifiee: string | null;
  duree_estimee: number | null;
  duree_reelle: number | null;
  cout_total: number | null;
  note_satisfaction: number | null;
  demandeur_id: string;
  service_demandeur_id: string | null;
  equipement_id: string | null;
  technicien_affecte_id: string | null;
  prestataire_id: string | null;
  mode_affectation: string | null;
  created_at: string;
  updated_at: string;
  demandeur: { id: string; first_name: string; last_name: string } | null;
  service_demandeur: { id: string; nom_complet: string } | null;
  equipement: { id: string; code_equipement: string; designation: string } | null;
  technicien: { id: string; first_name: string; last_name: string } | null;
  prestataire: { id: string; nom: string } | null;
}

interface Pagination {
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
}

interface Stats {
  total: number;
  by_statut: Array<{ statut: string; count: number }>;
  by_priorite: Array<{ priorite: string; count: number }>;
  urgences_ouvertes: number;
  duree_moyenne_minutes: number | null;
  taux_satisfaction: number | null;
  nb_evaluations: number;
  top_equipements: Array<{
    id: string | null;
    code_equipement?: string;
    designation?: string;
    count: number;
  }>;
  top_services: Array<{
    id: string | null;
    code?: string;
    nom_complet?: string;
    count: number;
  }>;
  tendance_mensuelle: Array<{ mois: string; count: number }>;
}

interface ServiceOption {
  id: string;
  code: string;
  nom_complet: string;
}

interface PersonnelOption {
  id: string;
  first_name: string;
  last_name: string;
}

interface PrestataireOption {
  id: string;
  nom: string;
}

type SortBy = 'date_demande' | 'numero_demande' | 'priorite' | 'statut' | 'titre' | 'created_at';
type SortOrder = 'asc' | 'desc';

interface FiltersState {
  search: string;
  priorite: string;
  type_demande: string;
  service_demandeur_id: string;
  equipement_id: string;
  technicien_affecte_id: string;
  demandeur_id: string;
  prestataire_id: string;
  date_demande_from: string;
  date_demande_to: string;
}

const EMPTY_FILTERS: FiltersState = {
  search: '',
  priorite: '',
  type_demande: '',
  service_demandeur_id: '',
  equipement_id: '',
  technicien_affecte_id: '',
  demandeur_id: '',
  prestataire_id: '',
  date_demande_from: '',
  date_demande_to: '',
};

const TABS: Array<{ key: string; label: string; statuts: InterventionStatut[] | null }> = [
  { key: 'toutes', label: 'Toutes', statuts: null },
  { key: 'nouvelles', label: 'Nouvelles', statuts: ['nouveau'] },
  { key: 'a-planifier', label: 'À planifier', statuts: ['affecte'] },
  {
    key: 'en-cours',
    label: 'En cours',
    statuts: ['en_cours', 'planifie', 'en_attente_pieces'],
  },
  {
    key: 'en-attente-validation',
    label: 'En attente validation',
    statuts: ['en_attente_validation'],
  },
  {
    key: 'a-cloturer',
    label: 'À clôturer',
    statuts: ['en_attente_validation_finale'],
  },
  { key: 'cloturees', label: 'Clôturées', statuts: ['cloture'] },
  { key: 'annulees', label: 'Annulées', statuts: ['annule'] },
];

export function InterventionsPage() {
  const { can } = usePermissions();
  const canRead = can('interventions', 'read');
  const canCreate = can('interventions', 'create');

  const [items, setItems] = useState<InterventionRow[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [stats, setStats] = useState<Stats | null>(null);
  const [mesValidationsCount, setMesValidationsCount] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [filters, setFilters] = useState<FiltersState>(EMPTY_FILTERS);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('toutes');

  const [sortBy, setSortBy] = useState<SortBy>('date_demande');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [page, setPage] = useState(1);

  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  async function handleExport() {
    setExporting(true);
    setErrorMessage(null);
    try {
      const res = await api.get('/interventions/export.xlsx', { responseType: 'blob' });
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `interventions-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setExporting(false);
    }
  }

  const [services, setServices] = useState<ServiceOption[]>([]);
  const [personnels, setPersonnels] = useState<PersonnelOption[]>([]);
  const [prestataires, setPrestataires] = useState<PrestataireOption[]>([]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(filters.search.trim()), 300);
    return () => clearTimeout(t);
  }, [filters.search]);

  useEffect(() => {
    setPage(1);
  }, [
    debouncedSearch,
    filters.priorite,
    filters.type_demande,
    filters.service_demandeur_id,
    filters.equipement_id,
    filters.technicien_affecte_id,
    filters.demandeur_id,
    filters.prestataire_id,
    filters.date_demande_from,
    filters.date_demande_to,
    activeTab,
    sortBy,
    sortOrder,
  ]);

  useEffect(() => {
    if (!canRead) return;
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    debouncedSearch,
    filters.priorite,
    filters.type_demande,
    filters.service_demandeur_id,
    filters.equipement_id,
    filters.technicien_affecte_id,
    filters.demandeur_id,
    filters.prestataire_id,
    filters.date_demande_from,
    filters.date_demande_to,
    activeTab,
    sortBy,
    sortOrder,
    page,
  ]);

  useEffect(() => {
    if (!canRead) return;
    loadStats();
    loadMesValidations();
  }, [canRead]);

  useEffect(() => {
    if (!canRead) return;
    api
      .get<{ data: ServiceOption[] }>('/services?statut=actif&page_size=200')
      .then((res) => setServices(res.data.data))
      .catch(() => setServices([]));
    api
      .get<{ data: PersonnelOption[] }>('/personnels?status=active&page_size=200')
      .then((res) => setPersonnels(res.data.data))
      .catch(() => setPersonnels([]));
    api
      .get<{ data: PrestataireOption[] }>('/prestataires?is_active=true&page_size=200')
      .then((res) => setPrestataires(res.data.data))
      .catch(() => setPrestataires([]));
  }, [canRead]);

  async function refresh() {
    setLoading(true);
    setErrorMessage(null);
    try {
      const params = new URLSearchParams();
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (filters.priorite) params.set('priorite', filters.priorite);
      if (filters.type_demande) params.set('type_demande', filters.type_demande);
      if (filters.service_demandeur_id)
        params.set('service_demandeur_id', filters.service_demandeur_id);
      if (filters.equipement_id) params.set('equipement_id', filters.equipement_id);
      if (filters.technicien_affecte_id)
        params.set('technicien_affecte_id', filters.technicien_affecte_id);
      if (filters.demandeur_id) params.set('demandeur_id', filters.demandeur_id);
      if (filters.prestataire_id) params.set('prestataire_id', filters.prestataire_id);
      if (filters.date_demande_from) {
        const iso = new Date(filters.date_demande_from).toISOString();
        params.set('date_demande_from', iso);
      }
      if (filters.date_demande_to) {
        const iso = new Date(filters.date_demande_to).toISOString();
        params.set('date_demande_to', iso);
      }

      const currentTab = TABS.find((t) => t.key === activeTab);
      if (currentTab?.statuts) {
        params.set('statut', currentTab.statuts.join(','));
      }

      params.set('sort_by', sortBy);
      params.set('sort_order', sortOrder);
      params.set('page', String(page));
      params.set('page_size', '50');
      const res = await api.get<{ data: InterventionRow[]; pagination: Pagination }>(
        `/interventions?${params.toString()}`,
      );
      setItems(res.data.data);
      setPagination(res.data.pagination);
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  async function loadStats() {
    try {
      const res = await api.get<{ data: Stats }>('/interventions/stats');
      setStats(res.data.data);
    } catch {
      // Silencieux.
    }
  }

  async function loadMesValidations() {
    try {
      const res = await api.get<{ data: unknown[] }>('/interventions/validations/mes-validations');
      setMesValidationsCount(res.data.data.length);
    } catch {
      // Silencieux.
    }
  }

  function toggleSort(col: SortBy) {
    if (sortBy === col) setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    else {
      setSortBy(col);
      setSortOrder('asc');
    }
  }

  function refreshAll() {
    refresh();
    loadStats();
    loadMesValidations();
  }

  function updateFilter<K extends keyof FiltersState>(key: K, value: FiltersState[K]) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  function applySavedFilter(payload: Record<string, unknown>) {
    setFilters({
      ...EMPTY_FILTERS,
      ...(payload as Partial<FiltersState>),
    });
  }

  const currentFilters = useMemo(() => {
    const out: Record<string, unknown> = {};
    (Object.keys(filters) as Array<keyof FiltersState>).forEach((k) => {
      const v = filters[k];
      if (v !== '' && v !== null && v !== undefined) out[k] = v;
    });
    return out;
  }, [filters]);

  const activeFilterCount = Object.keys(currentFilters).filter((k) => k !== 'search').length;

  const tabCounts = useMemo(() => {
    if (!stats) return new Map<string, number>();
    const byStatut = new Map<string, number>();
    for (const s of stats.by_statut) byStatut.set(s.statut, s.count);
    const counts = new Map<string, number>();
    counts.set('toutes', stats.total);
    for (const tab of TABS) {
      if (!tab.statuts) continue;
      const sum = tab.statuts.reduce((acc, st) => acc + (byStatut.get(st) ?? 0), 0);
      counts.set(tab.key, sum);
    }
    return counts;
  }, [stats]);

  if (!canRead) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <p className="text-sm text-gray-500">Vous n'avez pas accès au module Interventions.</p>
      </div>
    );
  }

  const totalLabel = pagination
    ? `${pagination.total} intervention(s)`
    : `${items.length} intervention(s) chargée(s)`;

  const enCoursCount =
    (stats?.by_statut.find((s) => s.statut === 'en_cours')?.count ?? 0) +
    (stats?.by_statut.find((s) => s.statut === 'planifie')?.count ?? 0) +
    (stats?.by_statut.find((s) => s.statut === 'en_attente_pieces')?.count ?? 0);

  const attenteValidationCount =
    (stats?.by_statut.find((s) => s.statut === 'en_attente_validation')?.count ?? 0) +
    (stats?.by_statut.find((s) => s.statut === 'en_attente_validation_finale')?.count ?? 0);

  return (
    <div className="max-w-7xl mx-auto space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <HardHat className="w-7 h-7 mr-2 text-primary-600" />
            Interventions
          </h1>
          <p className="text-sm text-gray-500 mt-1">{totalLabel}</p>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={refreshAll}
            className="inline-flex items-center p-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50"
            title="Rafraîchir"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <Link
            to="/interventions/urgences"
            className="inline-flex items-center px-3 py-2 border border-red-200 rounded-lg text-sm text-red-700 bg-red-50 hover:bg-red-100"
          >
            <AlertOctagon className="w-4 h-4 mr-1.5" />
            Urgences
            {stats && stats.urgences_ouvertes > 0 && (
              <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-4 px-1 text-[10px] font-bold bg-red-600 text-white rounded-full">
                {stats.urgences_ouvertes}
              </span>
            )}
          </Link>
          <button
            onClick={handleExport}
            disabled={exporting}
            className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-60"
            title="Exporter en Excel (import non disponible pour les interventions)"
          >
            {exporting ? (
              <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-1.5" />
            )}
            Exporter
          </button>
          {canCreate && (
            <button
              onClick={() => setCreateOpen(true)}
              className="inline-flex items-center px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg text-sm font-medium"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle demande
            </button>
          )}
        </div>
      </div>

      {errorMessage && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{errorMessage}</p>
        </div>
      )}

      {/* Bandeaux d'alerte */}
      {stats && stats.urgences_ouvertes > 0 && (
        <Link
          to="/interventions/urgences"
          className="block rounded-xl border border-red-200 bg-red-50 p-4 hover:bg-red-100 transition-colors"
        >
          <p className="text-sm font-medium text-red-900 flex items-center">
            <AlertOctagon className="w-4 h-4 mr-2" />
            {stats.urgences_ouvertes} urgence{stats.urgences_ouvertes > 1 ? 's' : ''} P0/P1 en cours
          </p>
          <p className="text-xs text-red-800 mt-1">Cliquez pour consulter la file d'urgences →</p>
        </Link>
      )}
      {mesValidationsCount > 0 && (
        <Link
          to="/mes-validations"
          className="block rounded-xl border border-yellow-200 bg-yellow-50 p-4 hover:bg-yellow-100 transition-colors"
        >
          <p className="text-sm font-medium text-yellow-900 flex items-center">
            <ShieldCheck className="w-4 h-4 mr-2" />
            Vous avez {mesValidationsCount} validation
            {mesValidationsCount > 1 ? 's' : ''} en attente
          </p>
          <p className="text-xs text-yellow-800 mt-1">Cliquez pour répondre →</p>
        </Link>
      )}

      {/* KPIs */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard label="Total" value={stats.total} icon={HardHat} tone="primary" />
          <KpiCard label="En cours" value={enCoursCount} icon={PlayCircle} tone="teal" />
          <KpiCard
            label="En attente validation"
            value={attenteValidationCount}
            icon={Hourglass}
            tone="orange"
          />
          <KpiCard
            label="Urgences ouvertes"
            value={stats.urgences_ouvertes}
            icon={AlertOctagon}
            tone="red"
            to="/interventions/urgences"
          />
        </div>
      )}

      {/* Onglets */}
      <div className="border-b border-gray-200 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <nav className="flex">
          {TABS.map((tab) => {
            const count = tabCounts.get(tab.key);
            const active = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-3 py-2 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  active
                    ? 'text-primary-600 border-primary-500 bg-primary-50/50'
                    : 'text-gray-600 border-transparent hover:text-gray-900'
                }`}
              >
                {tab.label}
                {typeof count === 'number' && (
                  <span
                    className={`ml-1.5 inline-flex items-center justify-center min-w-[18px] h-4 px-1 text-[10px] font-bold rounded-full ${
                      active ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-700'
                    }`}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Toolbar filtres */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            <SavedFilterMenu currentFilters={currentFilters} onApply={applySavedFilter} />
            <select
              value={filters.priorite}
              onChange={(e) => updateFilter('priorite', e.target.value)}
              className={selectClass}
            >
              <option value="">Toutes priorités</option>
              {INTERVENTION_PRIORITES.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
            <select
              value={filters.type_demande}
              onChange={(e) => updateFilter('type_demande', e.target.value)}
              className={selectClass}
            >
              <option value="">Tous types</option>
              {INTERVENTION_TYPES_DEMANDE.map((t) => (
                <option key={t} value={t}>
                  {typeDemandeLabel(t)}
                </option>
              ))}
            </select>
            <select
              value={filters.service_demandeur_id}
              onChange={(e) => updateFilter('service_demandeur_id', e.target.value)}
              className={selectClass}
            >
              <option value="">Tous services</option>
              {services.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.code} — {s.nom_complet}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher…"
                value={filters.search}
                onChange={(e) => updateFilter('search', e.target.value)}
                className="pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg w-52 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <button
              type="button"
              onClick={() => setShowAdvanced((s) => !s)}
              className={`inline-flex items-center px-3 py-1.5 border rounded-lg text-sm font-medium ${
                showAdvanced || activeFilterCount > 0
                  ? 'border-primary-300 bg-primary-50 text-primary-700'
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <SlidersHorizontal className="w-3.5 h-3.5 mr-1.5" />
              Plus de filtres
              {activeFilterCount > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center w-4 h-4 text-[10px] font-bold bg-primary-600 text-white rounded-full">
                  {activeFilterCount}
                </span>
              )}
            </button>
          </div>
        </div>

        {showAdvanced && (
          <div className="border-t border-gray-100 pt-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <AdvField label="Technicien">
                <select
                  value={filters.technicien_affecte_id}
                  onChange={(e) => updateFilter('technicien_affecte_id', e.target.value)}
                  className={selectClass + ' w-full'}
                >
                  <option value="">— Tous —</option>
                  {personnels.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.last_name} {p.first_name}
                    </option>
                  ))}
                </select>
              </AdvField>
              <AdvField label="Demandeur">
                <select
                  value={filters.demandeur_id}
                  onChange={(e) => updateFilter('demandeur_id', e.target.value)}
                  className={selectClass + ' w-full'}
                >
                  <option value="">— Tous —</option>
                  {personnels.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.last_name} {p.first_name}
                    </option>
                  ))}
                </select>
              </AdvField>
              <AdvField label="Prestataire">
                <select
                  value={filters.prestataire_id}
                  onChange={(e) => updateFilter('prestataire_id', e.target.value)}
                  className={selectClass + ' w-full'}
                >
                  <option value="">— Tous —</option>
                  {prestataires.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.nom}
                    </option>
                  ))}
                </select>
              </AdvField>
              <AdvField label="Date demande (du)">
                <input
                  type="date"
                  value={filters.date_demande_from}
                  onChange={(e) => updateFilter('date_demande_from', e.target.value)}
                  className={selectClass + ' w-full'}
                />
              </AdvField>
              <AdvField label="Date demande (au)">
                <input
                  type="date"
                  value={filters.date_demande_to}
                  onChange={(e) => updateFilter('date_demande_to', e.target.value)}
                  className={selectClass + ' w-full'}
                />
              </AdvField>
            </div>
            <div className="flex justify-end mt-3">
              <button
                onClick={() => setFilters(EMPTY_FILTERS)}
                className="inline-flex items-center text-xs text-gray-600 hover:text-gray-900"
              >
                <Filter className="w-3.5 h-3.5 mr-1" />
                Réinitialiser
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <SortHeader
                  label="N°"
                  col="numero_demande"
                  {...{ sortBy, sortOrder, toggleSort }}
                />
                <SortHeader label="Titre" col="titre" {...{ sortBy, sortOrder, toggleSort }} />
                <th className="hidden md:table-cell px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <SortHeader
                  label="Priorité"
                  col="priorite"
                  {...{ sortBy, sortOrder, toggleSort }}
                />
                <SortHeader label="Statut" col="statut" {...{ sortBy, sortOrder, toggleSort }} />
                <th className="hidden lg:table-cell px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Demandeur
                </th>
                <th className="hidden xl:table-cell px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Technicien
                </th>
                <SortHeader
                  label="Date"
                  col="date_demande"
                  className="hidden md:table-cell"
                  {...{ sortBy, sortOrder, toggleSort }}
                />
                <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-12" />
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={9} className="px-4 py-12 text-center">
                    <Loader2 className="w-6 h-6 animate-spin text-primary-500 mx-auto" />
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-12 text-center text-sm text-gray-500">
                    Aucune intervention. Créez la première pour démarrer.
                  </td>
                </tr>
              ) : (
                items.map((it) => (
                  <tr
                    key={it.id}
                    onClick={() => setDetailId(it.id)}
                    className="hover:bg-gray-50 cursor-pointer"
                  >
                    <td className="px-3 py-2.5 text-xs font-semibold text-primary-600 uppercase tracking-wide whitespace-nowrap">
                      {it.numero_demande}
                    </td>
                    <td className="px-3 py-2.5 text-gray-900 max-w-[260px] truncate">{it.titre}</td>
                    <td className="hidden md:table-cell px-3 py-2.5 text-gray-600">
                      {typeDemandeLabel(it.type_demande)}
                    </td>
                    <td className="px-3 py-2.5">
                      <PrioriteBadge priorite={it.priorite} compact />
                    </td>
                    <td className="px-3 py-2.5">
                      <StatutInterventionBadge statut={it.statut} />
                    </td>
                    <td className="hidden lg:table-cell px-3 py-2.5 text-gray-600 truncate max-w-[160px]">
                      {it.demandeur ? `${it.demandeur.first_name} ${it.demandeur.last_name}` : '—'}
                    </td>
                    <td className="hidden xl:table-cell px-3 py-2.5 text-gray-600 truncate max-w-[160px]">
                      {it.technicien
                        ? `${it.technicien.first_name} ${it.technicien.last_name}`
                        : it.prestataire
                          ? it.prestataire.nom
                          : '—'}
                    </td>
                    <td className="hidden md:table-cell px-3 py-2.5 text-gray-600 whitespace-nowrap">
                      <span className="inline-flex items-center">
                        <Clock className="w-3 h-3 mr-1 text-gray-400" />
                        {new Date(it.date_demande).toLocaleDateString()}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-right text-gray-400">
                      <ChevronRight className="w-4 h-4 inline" />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {pagination && pagination.total_pages > 1 && (
          <div className="flex items-center justify-between p-3 border-t border-gray-200 text-sm">
            <span className="text-gray-500">
              Page {pagination.page} sur {pagination.total_pages}
            </span>
            <div className="flex items-center space-x-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={pagination.page === 1}
                className="p-1.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setPage((p) => Math.min(pagination.total_pages, p + 1))}
                disabled={pagination.page === pagination.total_pages}
                className="p-1.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {createOpen && (
        <InterventionFormModal
          onClose={() => setCreateOpen(false)}
          onSaved={(id) => {
            setCreateOpen(false);
            refreshAll();
            if (id) setDetailId(id);
          }}
        />
      )}

      {detailId && (
        <InterventionDetailDrawer
          interventionId={detailId}
          onClose={() => setDetailId(null)}
          onRefresh={refreshAll}
        />
      )}
    </div>
  );
}

const selectClass =
  'px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50 disabled:text-gray-400';

function SortHeader({
  label,
  col,
  sortBy,
  sortOrder,
  toggleSort,
  className,
}: {
  label: string;
  col: SortBy;
  sortBy: SortBy;
  sortOrder: SortOrder;
  toggleSort(col: SortBy): void;
  className?: string;
}) {
  const active = sortBy === col;
  return (
    <th
      onClick={() => toggleSort(col)}
      className={`px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none ${className ?? ''}`}
    >
      <span className="inline-flex items-center">
        {label}
        {active ? (
          sortOrder === 'asc' ? (
            <ArrowUp className="w-3 h-3 ml-1" />
          ) : (
            <ArrowDown className="w-3 h-3 ml-1" />
          )
        ) : null}
      </span>
    </th>
  );
}

function AdvField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
      {children}
    </div>
  );
}

function KpiCard({
  label,
  value,
  icon: Icon,
  tone,
  to,
}: {
  label: string;
  value: number;
  icon: typeof HardHat;
  tone: 'default' | 'primary' | 'green' | 'red' | 'orange' | 'purple' | 'teal';
  to?: string;
}) {
  const tones: Record<typeof tone, string> = {
    default: 'text-gray-700',
    primary: 'text-primary-600',
    green: 'text-green-600',
    red: 'text-red-600',
    orange: 'text-orange-600',
    purple: 'text-purple-600',
    teal: 'text-teal-600',
  };
  const content = (
    <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm hover:shadow transition-shadow">
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
        <Icon className={`w-4 h-4 ${tones[tone]}`} />
      </div>
      <p className={`text-2xl font-bold mt-1 ${tones[tone]}`}>{value}</p>
    </div>
  );
  return to ? <Link to={to}>{content}</Link> : content;
}
